import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import FormMonitoring from "components/FormMonitoring";
import { CommonErrorsTexts, CommonTexts, ParticipantsTexts, Sex, patientsTexts } from "locale/es";
import { useRef, useState } from "react";
import { AppColors } from "resources/AppColors";
import { format } from "date-fns";
import { z } from "zod";
import { zodInputNumberPipe } from "types";
import DatePickerNew from "components/DatePickers/DatePickerNew/DatePickerNew";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const sexTypes = ["MAN", "WOMAN", "OTHER"];

const HeightNumber = zodInputNumberPipe(z.number().int().gt(0).optional());

// * Optional field with a minimum length of 2 characters if input exists
const lastNameType = z.string().superRefine((val, ctx) => {
  if (val.length === 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 2,
      type: "string",
      inclusive: true,
      message: patientsTexts.formInputErrors.lastName,
    });
  }

  return val
})

const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;

const passwordType = z.string().superRefine((val, ctx) => {
  if (val.length > 0 && !passwordRegex.test(val)) {
    ctx.addIssue({
      code: z.ZodIssueCode.invalid_string,
      type: "string",
      message: patientsTexts.formInputErrors.password,
    });
    return z.NEVER
  }
})

const NewParticipantSchema = z.object({
  firstName: z.string().min(2, patientsTexts.formInputErrors.firstName),
  lastName: lastNameType,
  email: z.string().min(1, CommonErrorsTexts.required).email({ message: patientsTexts.formInputErrors.email }),
  password: passwordType,
  confirmPassword: z.string(),
  birthDay: z.date({
    errorMap: (issue, { defaultError }) => ({
      message: issue.code === "invalid_date"
        ? CommonErrorsTexts.date.invalidDate
        : issue.code === "invalid_type"
          ? CommonErrorsTexts.date.date
          : defaultError,
    }),
  })
    .min(new Date('01/01/1900'), { message: CommonErrorsTexts.date.minDate })
    .max(new Date(), { message: CommonErrorsTexts.date.maxDate }),
  height: HeightNumber,
  sex: z.enum(sexTypes),
})
  .refine((data) => data.password === data.confirmPassword, {
    message: patientsTexts.formInputErrors.confirmPassword,
    path: ["confirmPassword"], // path of error
  })
  .transform(data => ({
    ...data,
    lastName: data.lastName || undefined,
    password: data.password || null,
    confirmPassword: undefined,
    birthDay: format(data.birthDay, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
  }))

export function PatientForm({
  allPatients,
  selectedCenterId,
  setShowModal,
  onSubmit,
  selectedPatient,
  isEdit = false
}) {
  const [formError, setFormError] = useState("");

  const { handleSubmit, control, getValues, reset } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(NewParticipantSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      firstName: selectedPatient?.user?.firstName || "",
      lastName: selectedPatient?.user?.lastName || "",
      email: selectedPatient?.user?.email || "",
      password: "",
      confirmPassword: "",
      birthDay: (selectedPatient?.birthDay && new Date(selectedPatient?.birthDay)) || null,
      height: selectedPatient?.height || "",
      sex: selectedPatient?.sex || ""
    },
  });

  const classes = useStyles();

  const emailInputRef = useRef()

  async function handleOnConfirmAddPatient(data) {
    try {
      const email = getValues("email")
      const participantEmailExists = allPatients.some((patient) => {
        if (allPatients.length === 0) return false;

        return patient.user?.email === email;
      });

      if (!isEdit && participantEmailExists) {
        emailInputRef.current.focus()
        throw new Error(ParticipantsTexts.EMAIL_REPEAT_ERROR);
      } else if (formError && !formError.response) {
        setFormError("")
      }

      // * Need to await for the promise to resolve or to throw
      await onSubmit(data);

      setShowModal(false);
    } catch (error) {
      setFormError(error);
    }
  };
  
  return (
    <FormMonitoring
      name='addPatient'
      submitForm={handleSubmit((data) => handleOnConfirmAddPatient({ ...data, centerId: selectedCenterId, login: data.email }))}
      resetFormInputs={reset}
      autoComplete='off'
      isEdit={isEdit}
    >
      <Controller
        control={control}
        name='firstName'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='text'
            InputLabelProps={{
              required: true
            }}
            label={CommonTexts.NAME}
            margin='normal'
            variant='outlined'
            autoFocus
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />
      <Controller
        control={control}
        name='lastName'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='text'
            label={CommonTexts.SURNAME}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />
      <Controller
        control={control}
        name='email'
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error }
        }) => (
          <TextField
            type='email'
            value={value}
            inputProps={{
              ref: emailInputRef
            }}
            InputLabelProps={{
              required: true
            }}
            onChange={onChange}
            onBlur={onBlur}
            label={CommonTexts.EMAIL}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            autoComplete='off'
            error={Boolean(error) || Boolean(formError && !formError.response)}
            helperText={error?.message || (formError && !formError.response) ? error?.message || formError.message : ""} />
        )}
      />
      <Controller
        control={control}
        name='password'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='password'
            label={CommonTexts.PASSWORD}
            InputLabelProps={{
              required: field.value.length > 0 && true
            }}
            margin='normal'
            variant='outlined'
            autoComplete='new-password'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""} />
        )}
      />
      <Controller
        control={control}
        name='confirmPassword'
        render={({
          field,
          fieldState: { error },
          formState
        }) => (
          <TextField
            {...field}
            type='password'
            label={CommonTexts.CONFIRM_PASSWORD}
            InputLabelProps={{
              required: Boolean(formState.dirtyFields.password) // ! Only triggered after user input in this field
            }}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ""} />
        )}
      />
      <Controller
        control={control}
        name='birthDay'
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error },
        }) => (
          <FormControl
            sx={{ mt: 2, mb: 1 }}
            className={classes.textFieldLabel}
          >
            <DatePickerNew
              label={CommonTexts.BIRTHDAY}
              maxDate={new Date()}
              minDate={new Date('01/01/1900')}
              inputRef={ref}
              value={value}
              setValue={onChange}
              required={true}
              textfieldProps={{
                error: Boolean(error),
                helperText: error?.message ?? "",
                onBlur: onBlur
              }} />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name='height'
        render={({
          field,
          fieldState: { error },
        }) => (
          <TextField
            {...field}
            type='number'
            label={CommonTexts.HEIGHT_IN_CM}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            inputProps={{
              min: 1
            }}
            error={Boolean(error)}
            helperText={error?.message ?? ""}
          />
        )}
      />
      <Controller
        control={control}
        name='sex'
        render={({
          field,
          fieldState: { error },
        }) => (
          <FormControl sx={{ mt: 2, mb: 1 }} className={classes.textFieldLabel}>
            <InputLabel required id='select-sex-label'>{Sex.SEX}</InputLabel>
            <Select
              {...field}
              labelId='sex-selector-label'
              id='sex-selector'
              data-testid='name-select'
              variant='outlined'
              label={CommonTexts.SEX}
              error={Boolean(error)}
              inputProps={{
                "data-testid": "select-input",
              }}
            >
              <MenuItem value=''>
                <em>{CommonTexts.SELECT_SEX}</em>
              </MenuItem>
              <MenuItem value={sexTypes[0]} data-testid='MAN'>
                {Sex.MAN}
              </MenuItem>
              <MenuItem value={sexTypes[1]}>{Sex.WOMAN}</MenuItem>
              <MenuItem value={sexTypes[2]}>{Sex.OTHER}</MenuItem>
            </Select>
            <FormHelperText
              id="my-helper-text"
              error={Boolean(error)}
            >
              {error && CommonErrorsTexts.sexType}
            </FormHelperText>

          </FormControl>
        )}
      />
      <Typography mt={1} variant='body' sx={{ color: "red" }}>
        {formError.response && patientsTexts.CREATION_ERROR}
      </Typography>
    </FormMonitoring>
  );
};
